import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { fetchThisWeekEvents } from "@/lib/queries";
import { TOPIC_LABEL, type Topic } from "@/lib/topics";

import { SeoNote } from "@/components/SeoNote";
import { normalizeTitle } from "@/lib/normalize-title";

export const Route = createFileRoute("/det-sker")({
  head: () => ({
    meta: [
      { title: "Stunder der venter – Flâneriet" },
      { name: "description", content: "Arrangementer i indeværende uge i Danmark: jazz, vin, kunst, litteratur og mere – udvalgt til den nysgerrige." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Flâneriet" },
      { property: "og:title", content: "Det sker – Flâneriet" },
      { property: "og:description", content: "Arrangementer i indeværende uge i Danmark: jazz, vin, kunst, litteratur og mere." },
      { property: "og:url", content: "https://flaneriet.online/det-sker" },
      { property: "og:image", content: "https://flaneriet.online/og/og-det-sker.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Flâneriet – Det sker" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Det sker – Flâneriet" },
      { name: "twitter:description", content: "Arrangementer i indeværende uge i Danmark: jazz, vin, kunst, litteratur og mere." },
      { name: "twitter:image", content: "https://flaneriet.online/og/og-det-sker.png" },
    ],
    links: [{ rel: "canonical", href: "https://flaneriet.online/det-sker" }],
  }),
  component: DetSkerPage,
});


const WEEKDAYS_LONG = ["søndag", "mandag", "tirsdag", "onsdag", "torsdag", "fredag", "lørdag"];
const MONTHS_LONG = ["januar", "februar", "marts", "april", "maj", "juni", "juli", "august", "september", "oktober", "november", "december"];

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function dayHeading(d: Date, todayKey: string, tomorrowKey: string): string {
  const key = dayKey(d);
  const weekday = WEEKDAYS_LONG[d.getDay()];
  const dateStr = `${d.getDate()}. ${MONTHS_LONG[d.getMonth()]}`;
  const cap = weekday.charAt(0).toUpperCase() + weekday.slice(1);
  if (key === todayKey) return `I dag · ${cap} ${dateStr}`;
  if (key === tomorrowKey) return `I morgen · ${cap} ${dateStr}`;
  return `${cap} ${dateStr}`;
}

function DetSkerPage() {
  const events = useQuery({ queryKey: ["events-week"], queryFn: fetchThisWeekEvents });
  const list = events.data ?? [];

  // Skjul events der allerede er slut på i dag (starttid før nu),
  // så "i dag" viser det der stadig er foran læseren.
  const now = new Date();
  const upcoming = list.filter((e) => new Date(e.starts_at) >= new Date(now.getTime() - 30 * 60 * 1000));

  const today = new Date();
  const todayKey = dayKey(today);
  const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
  const tomorrowKey = dayKey(tomorrow);

  // Grupper pr. dags-dato (lokal tid)
  const groups = new Map<string, { date: Date; items: typeof upcoming }>();
  for (const e of upcoming) {
    const d = new Date(e.starts_at);
    const key = dayKey(d);
    const existing = groups.get(key);
    if (existing) existing.items.push(e);
    else groups.set(key, { date: new Date(d.getFullYear(), d.getMonth(), d.getDate()), items: [e] });
  }
  const orderedGroups = Array.from(groups.values()).sort((a, b) => a.date.getTime() - b.date.getTime());

  return (
    <div>
      <header className="pt-12 pb-6 border-b-[3px] border-b-double border-b-ink">
        <p className="eyebrow mb-2.5">I byen & omegn · denne uge</p>
        <h1 className="font-serif font-normal text-[2.4rem] md:text-[3rem] leading-[1.02] text-ink m-0">Det sker</h1>
        <p className="mt-3 meta italic max-w-xl">
          Arrangementer i denne uge på tværs af Danmark – jazz, vin, kunst, litteratur og andet, der er værd at gå ud efter.
        </p>
      </header>


      {orderedGroups.length > 0 ? (
        <div className="mt-8 space-y-10">
          {orderedGroups.map((group) => (
            <section key={dayKey(group.date)}>
              <h2 className="eyebrow border-b border-rule pb-2 mb-3">
                {dayHeading(group.date, todayKey, tomorrowKey)}
              </h2>
              <ul className="divide-y divide-rule">
                {group.items.map((e) => {
                  const d = new Date(e.starts_at);
                  const timeStr = d.toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" });
                  return (
                    <li key={e.id} className="py-5">
                      <a
                        href={e.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group grid gap-y-1 gap-x-4 grid-cols-[minmax(0,1fr)] md:grid-cols-[5rem_minmax(0,1fr)_auto] md:items-baseline"
                      >
                        <span className="eyebrow md:col-start-1 md:row-start-1">{timeStr}</span>
                        <span className="md:col-start-3 md:row-start-1 meta italic text-right hidden md:block">
                          {TOPIC_LABEL[e.topic as Topic]}
                        </span>
                        <span className="md:col-start-2 md:row-start-1 min-w-0">
                          <span className="font-serif text-[1.2rem] md:text-[1.3rem] block group-hover:text-bordeaux transition-colors duration-500 text-ink">
                            {normalizeTitle(e.title)}
                          </span>
                          <span className="meta italic block mt-1">
                            {[e.venue, e.city].filter(Boolean).join(", ") || e.source}
                          </span>
                        </span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      ) : (
        <p className="meta italic py-16 text-center">Ingen arrangementer fundet i denne uge endnu.</p>
      )}

      <SeoNote eyebrow="Om kalenderen">
        <p>
          Stunder der venter er Flâneriets ugentlige kalender over arrangementer i Danmark, som er
          værd at gå ud efter – jazzkoncerter, vinsmagninger, kunstudstillinger, forfatteraftener,
          fernisager, litterære oplæsninger og cykelture. Kurateret for den nysgerrige, der hellere
          går til én rigtig ting end tre på én aften.
        </p>
        <p>
          Kalenderen fornyes hver mandag. Klik på et arrangement for at komme direkte til
          arrangørens egen side, hvor du kan læse mere og reservere.
        </p>
      </SeoNote>
    </div>
  );
}
